/**
 * Helpers de Web Push: registro del service worker (public/sw.js, compartido con el
 * arranque sin red; ver utils/serviceWorker.ts) y manejo de la suscripción del
 * navegador.
 *
 * Soporte real: Android Chrome/Edge/Firefox completo; iOS Safari solo cuando la
 * app está instalada como PWA en home screen (iOS 16.4+).
 */
import { registerServiceWorker } from './serviceWorker';

// Recordamos la intención del usuario por dispositivo para re-suscribir en silencio
export const PUSH_ENABLED_KEY = 'orderReadyPushEnabled';

export const isPushSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  return registerServiceWorker();
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  // Sin argumento: la registración que controla la página, venga de sw.js o del
  // push-sw.js de antes de la fusión.
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

/** Convierte la VAPID public key (base64url) al Uint8Array que pide subscribe() */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface BrowserPushSubscription {
  endpoint: string;
  p256dh: string;
  auth: string;
  deviceInfo: string;
}

function toDto(subscription: PushSubscription): BrowserPushSubscription {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh || '',
    auth: json.keys?.auth || '',
    deviceInfo: navigator.userAgent.slice(0, 300),
  };
}

/**
 * Pide permiso (si hace falta) y crea/reusa la suscripción push del navegador.
 * Devuelve null si el usuario negó el permiso o el navegador no soporta push.
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<BrowserPushSubscription | null> {
  if (!isPushSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await registerPushServiceWorker();
  if (!registration) return null;

  // Espera a que el SW esté activo antes de suscribir
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
  }

  return toDto(subscription);
}

/** Cancela la suscripción local del navegador. Devuelve el endpoint cancelado (para avisar al server). */
export async function unsubscribeFromPush(): Promise<string | null> {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}
